import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { FaCheck } from 'react-icons/fa';
import {
  ARTICLE_SKUPINA_FIELD_HELP,
  articleToSkupinaValue,
  buildArticleSkupinaOptions,
  resolveArticleImportance,
  skupinaValueToGrouping,
} from '../../services/articleListGroups';
import { updateArticleGrouping } from '../../services/articlesService';
import './ArticleAdminControls.css';

const SAVED_FEEDBACK_MS = 2500;

function ArticleAdminControls({
  article,
  tenantId,
  tenantConfig,
  compact = false,
  tableCell = false,
  className = '',
  onSaved,
  showFieldHelp = false,
}) {
  const [skupinaValue, setSkupinaValue] = useState(() => articleToSkupinaValue(article));
  const [saveState, setSaveState] = useState('idle');
  const [error, setError] = useState('');
  const savedTimeoutRef = useRef(null);

  const skupinaOptions = useMemo(
    () => buildArticleSkupinaOptions(tenantConfig),
    [tenantConfig],
  );

  useEffect(() => {
    setSkupinaValue(articleToSkupinaValue(article));
  }, [article.id, article.listGroupId, article.importance]);

  useEffect(() => () => {
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
    }
  }, []);

  const scheduleSavedFeedback = () => {
    setSaveState('saved');
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
    }
    savedTimeoutRef.current = setTimeout(() => {
      setSaveState('idle');
      savedTimeoutRef.current = null;
    }, SAVED_FEEDBACK_MS);
  };

  const persist = async (nextSkupinaValue) => {
    const grouping = skupinaValueToGrouping(nextSkupinaValue, article);

    setSaveState('saving');
    setError('');
    try {
      await updateArticleGrouping(
        article.id,
        grouping,
        tenantId,
        { currentImportance: resolveArticleImportance(article) },
      );
      scheduleSavedFeedback();
      onSaved?.(grouping);
    } catch (saveError) {
      console.error('Error updating article grouping:', saveError);
      setSaveState('idle');
      setError(saveError.message || 'Shranjevanje ni uspelo.');
      setSkupinaValue(articleToSkupinaValue(article));
    }
  };

  const handleSkupinaChange = async (event) => {
    event.stopPropagation();
    const nextValue = event.target.value;
    setSkupinaValue(nextValue);
    await persist(nextValue);
  };

  const stopClick = (event) => {
    event.stopPropagation();
  };

  return (
    <div
      className={[
        'article-admin-controls',
        compact && 'article-admin-controls--compact',
        tableCell && 'article-admin-controls--table-cell',
        className,
      ].filter(Boolean).join(' ')}
      onClick={stopClick}
      onKeyDown={stopClick}
      role="presentation"
    >
      <div className="article-admin-controls__fields">
        <Form.Group className="article-admin-controls__field article-admin-controls__field--skupina">
          {!compact && !tableCell && (
            <Form.Label className="article-admin-controls__label">Skupina</Form.Label>
          )}
          <Form.Select
            size="sm"
            value={skupinaValue}
            onChange={handleSkupinaChange}
            disabled={saveState === 'saving'}
            aria-label="Skupina obrazca"
          >
            {skupinaOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          {showFieldHelp && (
            <Form.Text className="article-admin-controls__field-help">{ARTICLE_SKUPINA_FIELD_HELP}</Form.Text>
          )}
        </Form.Group>

        {(tableCell || saveState !== 'idle') && (
          <div
            className={`article-admin-controls__status article-admin-controls__status--${saveState}`}
            aria-live="polite"
            aria-atomic="true"
          >
            {saveState === 'saving' && (
              <>
                <Spinner animation="border" size="sm" className="article-admin-controls__status-spinner" />
                <span>Shranjujem…</span>
              </>
            )}
            {saveState === 'saved' && (
              <>
                <FaCheck aria-hidden="true" />
                <span>Shranjeno</span>
              </>
            )}
          </div>
        )}
      </div>

      {!compact && !tableCell && !showFieldHelp && (
        <p className="article-admin-controls__hint">{ARTICLE_SKUPINA_FIELD_HELP}</p>
      )}
      {error && <div className="article-admin-controls__error">{error}</div>}
    </div>
  );
}

export default ArticleAdminControls;
